import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { loadTeamData, TeamMember } from '../data/teamData';
import { useTeamStore } from '../hooks/useTeamStore';
import { calcSimilarity } from '../utils/similarity';

const MemberDetail: React.FC = () => {
  const { teamId, memberId } = useParams<{ teamId: string; memberId: string }>();
  const navigate = useNavigate();
  const resolvedTeamId = teamId || 'default';

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { claims, myClaimedMemberId, getMyHandshakesWith } = useTeamStore(resolvedTeamId);

  useEffect(() => {
    let cancelled = false;
    loadTeamData()
      .then(data => {
        if (!cancelled) {
          setMembers(data);
          setLoading(false);
        }
      })
      .catch(e => {
        if (!cancelled) {
          setError(e.message || '加载团队数据失败');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [resolvedTeamId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3A7D78] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  const member = members.find(m => m.id === memberId);
  if (!member) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-400">未找到该成员</p>
        <button
          onClick={() => navigate(`/team/${resolvedTeamId}`)}
          className="text-[#3A7D78] hover:underline text-sm"
        >
          返回卡片墙
        </button>
      </div>
    );
  }

  const isMe = memberId === myClaimedMemberId;
  const hsRecord = myClaimedMemberId && !isMe ? getMyHandshakesWith(memberId!) : undefined;

  if (!isMe && !hsRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-400">你还没有与这位成员握手，无法查看完整才干</p>
        <button
          onClick={() => navigate(`/team/${resolvedTeamId}`)}
          className="text-[#3A7D78] hover:underline text-sm"
        >
          返回卡片墙
        </button>
      </div>
    );
  }

  let score = 0;
  if (!isMe && myClaimedMemberId) {
    const my = members.find(m => m.id === myClaimedMemberId);
    if (my) {
      score = calcSimilarity(my.top10, my.bottom5, member.top10, member.bottom5);
    }
  }

  const memberNickname = claims[member.id] || '未认领';

  return (
    <div className="min-h-screen bg-gray-50 p-5">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <button
            onClick={() => navigate(`/team/${resolvedTeamId}`)}
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">返回卡片墙</span>
          </button>
        </motion.div>

        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {isMe ? '我的34才干排序' : `${memberNickname} 的34才干排序`}
          </h2>
          {!isMe && (
            <div className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-[#3A7D78]/10 rounded-full">
              <span className="text-[#3A7D78] font-bold text-lg">
                {score >= 0 ? '+' : ''}{score}%
              </span>
              <span className="text-sm text-gray-500">相似度</span>
            </div>
          )}
        </motion.div>

        <div className="grid md:grid-cols-4 gap-6">
          <motion.div
            className="bg-white rounded-xl shadow-sm p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            style={{ breakInside: 'avoid' }}
          >
            <h3 className="text-lg font-semibold mb-4 text-center text-gray-700">才干排序 (1-10)</h3>
            <ul className="space-y-2">
              {member.top10.map((talent, index) => (
                <li key={talent} className="p-3 bg-[#3A7D78]/10 text-[#3A7D78] rounded-lg text-center text-sm font-medium">
                  {index + 1}. {talent}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{ breakInside: 'avoid' }}
          >
            <h3 className="text-lg font-semibold mb-4 text-center text-gray-700">才干排序 (11-20)</h3>
            <ul className="space-y-2">
              {member.talents.slice(10, 20).map((talent, index) => (
                <li key={talent} className="p-3 bg-gray-100 rounded-lg text-center text-sm">
                  {index + 11}. {talent}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            style={{ breakInside: 'avoid' }}
          >
            <h3 className="text-lg font-semibold mb-4 text-center text-gray-700">才干排序 (21-29)</h3>
            <ul className="space-y-2">
              {member.talents.slice(20, 29).map((talent, index) => (
                <li key={talent} className="p-3 bg-gray-100 rounded-lg text-center text-sm">
                  {index + 21}. {talent}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{ breakInside: 'avoid' }}
          >
            <h3 className="text-lg font-semibold mb-4 text-center text-gray-700">才干排序 (30-34)</h3>
            <ul className="space-y-2">
              {member.bottom5.map((talent, index) => (
                <li key={talent} className="p-3 bg-gray-200 rounded-lg text-center text-sm">
                  {member.talents.length - 5 + index + 1}. {talent}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <button
            onClick={() => navigate(`/team/${resolvedTeamId}`)}
            className="px-6 py-3 bg-[#3A7D78] text-white font-medium rounded-xl hover:bg-[#2E6B67] transition-colors"
          >
            返回卡片墙继续握手
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default MemberDetail;
